/**
 * MembershipRenewalsReport (MEMBERSHIP-WORKLIST) — the renewal + win-back call
 * sheet. Two modes:
 *   • Expiring soon — active plans whose coverage ends within N days. Call them
 *     before they lapse (the renewal opportunity).
 *   • Lapsed — plans that expired within the last N days and weren't renewed.
 *     The win-back list (the churn research's strongest finding: active-plan
 *     shops retain 80-90% vs 20-60%, and a big chunk of "cancels" are just a
 *     plan that quietly expired).
 *
 * Derived entirely from ESC's own agreement dates (mirror_serv_agr, read-only) —
 * no manual "needs renewal" flag for anyone to maintain. Phone on the row; a
 * link straight to the customer's Agreements tab to renew. CSV for a call list.
 * Modeled on Warranty Expiring + Collection Manager (the worklist pattern).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, ExternalLink, ShieldCheck, RotateCcw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { downloadCsv } from "./csv";

const EXPIRING_WINDOWS = [30, 60, 90, 180];
const LAPSED_WINDOWS = [90, 180, 365, 730];
const iso = (d: Date) => d.toISOString().slice(0, 10);
const money = (v: number | null) => (v == null ? "—" : v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }));
function chunk<T>(a: T[], n: number): T[][] { const o: T[][] = []; for (let i = 0; i < a.length; i += n) o.push(a.slice(i, i + n)); return o; }
function yearsSince(s: string | null): number | null {
  if (!s) return null;
  const t = new Date(s + "T00:00:00"), n = new Date();
  let y = n.getFullYear() - t.getFullYear();
  const m = n.getMonth() - t.getMonth();
  if (m < 0 || (m === 0 && n.getDate() < t.getDate())) y -= 1;
  return Math.max(0, y);
}
function planLabel(t: string | null): string {
  if (!t) return "Plan";
  const m = /^(\d+)\s*YR$/i.exec(t.trim());
  return m ? `${m[1]}-Year` : t;
}

interface AgrRow {
  custno: string; locno: string | null; agrmt_no: string; agrmt_type: string | null;
  contr_amt: number | null; orig_contr: string | null; expire_date: string | null; sa_inactive: number | null;
  customer?: string; phone?: string | null; email?: string | null;
}

export function MembershipRenewalsReport() {
  const [mode, setMode] = useState<"expiring" | "lapsed">("expiring");
  const [days, setDays] = useState(90);
  const windows = mode === "expiring" ? EXPIRING_WINDOWS : LAPSED_WINDOWS;

  const { data, isFetching } = useQuery({
    queryKey: ["membership_renewals", mode, days],
    queryFn: async (): Promise<AgrRow[]> => {
      const today = iso(new Date());
      let q = supabase
        .from("mirror_serv_agr")
        .select("custno, locno, agrmt_no, agrmt_type, contr_amt, orig_contr, expire_date, sa_inactive");
      if (mode === "expiring") {
        const horizon = iso(new Date(Date.now() + days * 86400000));
        q = q.gte("expire_date", today).lte("expire_date", horizon).order("expire_date");
      } else {
        const back = iso(new Date(Date.now() - days * 86400000));
        q = q.gte("expire_date", back).lt("expire_date", today).order("expire_date", { ascending: false });
      }
      const { data: ags } = await q.limit(600);
      let rows = (ags ?? []) as AgrRow[];
      // Expiring = still in force (ESC sa_inactive uses -1 for true/inactive).
      if (mode === "expiring") rows = rows.filter((r) => r.sa_inactive !== -1);
      if (rows.length === 0) return [];
      const custnos = [...new Set(rows.map((r) => r.custno))];
      const cMap = new Map<string, string>();
      const lMap = new Map<string, { phone1?: string | null; email?: string | null }>();
      await Promise.all(
        chunk(custnos, 150).map(async (cs) => {
          const [{ data: custs }, { data: locs }] = await Promise.all([
            supabase.from("mirror_customer").select("custno, fullname").in("custno", cs),
            supabase.from("mirror_location").select("custno, locno, phone1, email").in("custno", cs),
          ]);
          for (const c of (custs ?? []) as { custno: string; fullname: string }[]) cMap.set(c.custno, c.fullname);
          for (const l of (locs ?? []) as Record<string, string>[]) lMap.set(`${l.custno}|${l.locno}`, l);
        }),
      );
      return rows.map((r) => {
        const l = lMap.get(`${r.custno}|${r.locno}`);
        return { ...r, customer: cMap.get(r.custno) ?? r.custno, phone: l?.phone1 ?? null, email: l?.email ?? null };
      });
    },
  });

  const rows = useMemo(() =>
    (data ?? []).map((r) => ({
      ...r,
      delta: r.expire_date ? Math.round((new Date(r.expire_date + "T00:00:00").getTime() - Date.now()) / 86400000) : 0,
      tenure: yearsSince(r.orig_contr),
    })), [data]);

  const totalValue = rows.reduce((s, r) => s + (r.contr_amt ?? 0), 0);

  const exportCsv = () => downloadCsv(`membership-${mode}-${days}d`, [
    [mode === "expiring" ? "Expires" : "Expired", "Days", "Plan", "Value", "Member since", "Years", "Customer", "Phone", "Email", "Customer #", "Agreement #"],
    ...rows.map((r) => [r.expire_date ?? "", r.delta, planLabel(r.agrmt_type), r.contr_amt ?? "", r.orig_contr ?? "", r.tenure ?? "", r.customer ?? "", r.phone ?? "", r.email ?? "", r.custno, r.agrmt_no]),
  ]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {/* Mode */}
        <div className="inline-flex overflow-hidden rounded-md border border-hairline">
          {([["expiring", "Expiring soon"], ["lapsed", "Lapsed"]] as const).map(([k, label]) => (
            <button key={k} type="button"
              onClick={() => { setMode(k); setDays(k === "expiring" ? 90 : 180); }}
              className={cn("px-3 py-1.5 text-sm", mode === k ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}>
              {label}
            </button>
          ))}
        </div>
        <span className="text-sm text-slate-500">{mode === "expiring" ? "within" : "in the last"}</span>
        <div className="inline-flex overflow-hidden rounded-md border border-hairline">
          {windows.map((w) => (
            <button key={w} type="button" onClick={() => setDays(w)}
              className={cn("px-3 py-1.5 text-sm", days === w ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}>
              {w >= 365 ? `${w / 365}y` : `${w}d`}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400">
          {rows.length} {rows.length === 1 ? "plan" : "plans"}
          {rows.length > 0 && <> · {money(totalValue)} {mode === "expiring" ? "up for renewal" : "to win back"}</>}
        </span>
        <InfoTip label="About this list">
          {mode === "expiring"
            ? "Active plans whose coverage ends in this window; call to renew before they lapse. Built from the agreement dates, so the list keeps itself current."
            : "Plans that expired and weren't renewed. The newest lapses come first because they're the easiest to win back."}
        </InfoTip>
        {rows.length > 0 && (
          <button type="button" onClick={exportCsv}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <Download className="h-4 w-4" /> CSV
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-hairline bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-3 py-2">{mode === "expiring" ? "Expires" : "Expired"}</th>
              <th className="px-3 py-2">{mode === "expiring" ? "Left" : "Ago"}</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2">Value</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Contact</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {isFetching && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">Loading…</td></tr>}
            {!isFetching && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">
                {mode === "expiring" ? "No plans expiring in this window." : "No lapsed plans in this window."}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={`${r.custno}-${r.agrmt_no}`} className="border-b border-hairline last:border-0 hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-2 text-slate-600">{r.expire_date}</td>
                <td className="px-3 py-2">
                  <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium",
                    mode === "expiring"
                      ? (r.delta <= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600")
                      : "bg-rose-50 text-rose-700")}>
                    {mode === "expiring" ? `${r.delta}d` : `${Math.abs(r.delta)}d`}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-700">
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-slate-300" />{planLabel(r.agrmt_type)}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">{money(r.contr_amt)}</td>
                <td className="px-3 py-2 text-slate-700">
                  {r.customer}
                  {r.tenure != null && <div className="text-xs text-slate-400">Plus member {r.tenure} {r.tenure === 1 ? "yr" : "yrs"}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {r.phone ? <a href={`tel:${r.phone}`} className="hover:text-brand-600">{r.phone}</a> : <span className="text-slate-300">no phone</span>}
                  {r.email ? <div className="truncate text-slate-400">{r.email}</div> : null}
                </td>
                <td className="px-3 py-2">
                  <Link to={`/customers/${r.custno}?loc=${r.locno ?? ""}&tab=agreements`} title="Open the agreement" className="text-slate-300 hover:text-brand-600">
                    {mode === "expiring" ? <ExternalLink className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
